import type { PersistedTemplatesSettings } from './chatSettingsStorage';

/**
 * The currently-available options a restore is validated against. Each list is the set of ids/slugs
 * that still exist. A list left `undefined` means "not known here — pass that field through unchanged"
 * (e.g. `templateSlugs` is reconciled by the templates options component, not this hook).
 */
export interface AvailableOptions {
  knowledgeBaseIds?: readonly string[];
  campaignIds?: readonly string[];
  guidelineIds?: readonly string[];
  templateSlugs?: readonly string[];
  allowedModels?: readonly string[];
}

/**
 * Prunes a persisted settings snapshot down to values that still exist, so restoring never applies a
 * dangling reference (deleted campaign / guideline / template / KB, or a now-disallowed model). Pure
 * and side-effect-free. Order matters for the campaign: it is only kept when it still resolves within
 * the RECONCILED knowledge bases (campaign files derive from the selected KBs).
 *
 * A field is dropped entirely (left `undefined`) when it no longer resolves — the UI then falls back
 * to its normal default/empty state instead of showing a broken selection. When a given list is
 * `undefined`, that field is passed through unchanged (not enough info to prune it here).
 */
export function reconcileTemplatesSettings(
  settings: PersistedTemplatesSettings,
  available: AvailableOptions,
): PersistedTemplatesSettings {
  const result: PersistedTemplatesSettings = {};

  if (available.knowledgeBaseIds === undefined) {
    if (settings.knowledgeBaseIds !== undefined)
      result.knowledgeBaseIds = settings.knowledgeBaseIds;
  } else {
    const kbSet = new Set(available.knowledgeBaseIds);
    const reconciledKbIds = (settings.knowledgeBaseIds ?? []).filter((id) => kbSet.has(id));
    if (reconciledKbIds.length > 0) result.knowledgeBaseIds = reconciledKbIds;
  }

  // Campaign is valid only if it still exists AND lives in a still-selected KB's files. `campaignIds`
  // is the set of campaign file ids across the reconciled KBs (computed by the caller).
  if (settings.selectedCampaignId !== undefined) {
    if (
      available.campaignIds === undefined ||
      available.campaignIds.includes(settings.selectedCampaignId)
    ) {
      result.selectedCampaignId = settings.selectedCampaignId;
    }
  }

  if (available.guidelineIds === undefined) {
    if (settings.selectedGuidelineIds !== undefined) {
      result.selectedGuidelineIds = settings.selectedGuidelineIds;
    }
  } else {
    const guidelineSet = new Set(available.guidelineIds);
    const reconciledGuidelineIds = (settings.selectedGuidelineIds ?? []).filter((id) =>
      guidelineSet.has(id),
    );
    if (reconciledGuidelineIds.length > 0) result.selectedGuidelineIds = reconciledGuidelineIds;
  }

  if (settings.selectedTemplate !== undefined) {
    if (
      available.templateSlugs === undefined ||
      available.templateSlugs.includes(settings.selectedTemplate)
    ) {
      result.selectedTemplate = settings.selectedTemplate;
    }
  }

  const model = settings.llmOptions?.model;
  if (model !== undefined) {
    if (available.allowedModels === undefined || available.allowedModels.includes(model)) {
      result.llmOptions = { model };
    }
  }

  // Purely local boolean — always safe to restore.
  if (settings.autoApplyQa !== undefined) result.autoApplyQa = settings.autoApplyQa;

  return result;
}
